import React, { useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import { useTranslation } from 'react-i18next';
import { MuiThemeProvider } from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
// Component to avoid a problem on this version,
// PAY ATTENTION ON UPDATE material-table
import { useSelector } from 'react-redux';
import EditableRow from './EditableRow';
import Header from './Header';
import icons from './icons';
import { config, localization, theme } from './config';
import preCell from './Cells/preCell';
import DefaultCell from './Cells';
import FilterLookupCell from './Cells/FilterLookupCell';
import TimeCell from './Cells/TimeCell';
import DateCell from './Cells/DateCell';
import ActiveCell from './Cells/ActiveCell';
import i18n from '~/i18n';
import { getColumnOrder, setColumnOrder } from '~/services/cache';
import DateFutureCell from './Cells/DateFutureCell';

const getOriginal = item => item?.tableData?.original || item;
const isEditingRow = item => item.tableData?.editing === 'update';
const isUncommittedAddRow = (rowData, columnId = 'id') => {
  if (rowData?.update_status === 'I') return true;
  const pk = rowData?.[columnId];
  if (pk === '' || pk == null) return false;
  return Number(pk) < 0;
};
const changeField = ({
  custom, row, value, field,
}) => ({
  [field]: value,
  ...(custom ? custom({ row, field, value }) : {}),
});

const isEditableOutsideMultiEdit = (editableMode, columnDef, rowData) => {
  if (typeof editableMode === 'function') {
    return editableMode(columnDef, rowData);
  }
  return editableMode !== 'never';
};

/**
 * Step 1 — apply the typed value to every row in multi-edit (`editing === 'update'`).
 * Does not validate. `hasInvalid` is left untouched so uniqueness checks can see
 * the new sibling values in a later step.
 */
const applyFieldToEditingRows = (rows, { customOnChange, field, value }) => (
  rows.map(item => (isEditingRow(item)
    ? {
      ...item,
      tableData: {
        ...item.tableData,
        editing: 'update',
        original: { ...getOriginal(item) },
      },
      ...changeField({
        custom: customOnChange, row: item, field, value,
      }),
    }
    : item))
);

/** True when any column.validate(item) reports isValid === false. */
const rowHasInvalid = (item, validateColumns) => (
  validateColumns.some(column => column.validate(item)?.isValid === false)
);

/**
 * Step 3 — stamp `tableData.hasInvalid` per editing row from column.validate.
 * Apply/Save only read this flag. Must run after Step 2 so checkDuplicated can
 * see other in-progress rows via getOriginalData().
 */
const stampEditingValidity = (rows, validateColumns) => (
  rows.map(item => (isEditingRow(item)
    ? {
      ...item,
      tableData: {
        ...item.tableData,
        hasInvalid: rowHasInvalid(item, validateColumns),
      },
    }
    : item))
);

/**
 * Step 2 — publish the in-memory snapshot so getOriginalData() sees sibling edits.
 * Writes dataManager + state.originalData only. Does not setState: a render here
 * would run cell validate / filters / selection before hasInvalid is restamped.
 */
const publishTableData = (tableRef, rows) => {
  const table = tableRef;
  table.dataManager.setData(rows);
  const nextRenderState = table.dataManager.getRenderState();
  if (table.state) {
    table.state.originalData = nextRenderState.originalData || rows;
  }
};

const PF2MDataTable = React.forwardRef(({
  data,
  columns,
  onAdd,
  onChange,
  onDiscard,
  onApply,
  onDelete,
  onInactivateAll,
  options,
  extraActions,
  defaultEditable,
  onExport,
  onImport,
  isEditable = () => true,
  ...propRest
}, ref) => {
  // TODO
  // Adjust consts names to set proper actions names and flows
  const { t: translate } = useTranslation();
  // Used to set and get the internal state of the table, consult material-table docs
  const materialTableRef = React.createRef();
  // Used to set current edit mode and check if the edit is multi edit
  const isMultiEditRef = useRef(false);
  // Used to store the pending row updates
  // The key is the row id and the value is the row data
  const pendingRowUpdatesRef = useRef({});
  // Used to store the current filter state
  const filterStateRef = useRef({
    filters: {},
    orderBy: -1,
    orderDirection: '',
    page: 0,
  });

  useImperativeHandle(ref, () => ({
    getOriginalData: () => materialTableRef.current?.state?.originalData || [],
  }));

  const user = useSelector(state => state.auth.user);
  const hasUniqueColumns = columns.some(col => col.unique);
  const isSelectionDisabled = rowData => (
    Boolean(options.editableRowValidation?.(rowData))
    || (hasUniqueColumns && isUncommittedAddRow(rowData, options.columnId || 'id'))
  );

  // replace null values with empty string
  const parsedData = dataList => (dataList.filter(i => i.update_status !== 'D').map(item => ({
    ...item,
    ...Object.keys(item)
      .reduce((acc, key) => ({ ...acc, [key]: item[key] === null ? '' : item[key] }), {}),
  })));

  // get the value in table's inner state
  const val = (cell) => {
    if (!cell.rowData.tableData) return cell.value;
    const { id } = cell.rowData.tableData;
    const innerRow = materialTableRef.current.state.renderData.find(i => i.tableData.id === id);
    return innerRow ? innerRow[cell.columnDef.field] : cell.value;
  };

  const saveState = (tableRef) => {
    if (!tableRef) return;
    const { state } = tableRef;
    filterStateRef.current = {
      filters: state.columns.reduce((acc, col) => {
        if (col.field && col.tableData?.filterValue !== undefined) {
          acc[col.field] = col.tableData.filterValue;
        }
        return acc;
      }, {}),
      orderBy: state.orderBy,
      orderDirection: state.orderDirection,
      page: state.currentPage || 0,
    };
  };

  const restoreState = (tableRef) => {
    if (!tableRef) return;

    const table = tableRef;

    const {
      filters, orderBy, orderDirection, page,
    } = filterStateRef.current;

    const restoredColumns = table.state.columns.map((col) => {
      if (col.field && filters[col.field] !== undefined) {
        return {
          ...col,
          tableData: {
            ...col.tableData,
            filterValue: filters[col.field],
          },
        };
      }

      return col;
    });

    const newState = {
      ...table.state,
      columns: restoredColumns,
      orderBy,
      orderDirection,
      currentPage: page,
    };

    if (table.dataManager) {
      table.dataManager.columns = restoredColumns;
      table.dataManager.orderBy = orderBy;
      table.dataManager.orderDirection = orderDirection;
      table.dataManager.currentPage = page;

      table.setState({
        ...table.dataManager.getRenderState(),
        ...newState,
      });
    }
  };

  const applyFiltersToColumns = (cols) => {
    const { filters } = filterStateRef.current;
    if (Object.keys(filters).length === 0) return cols;

    return cols.map((col) => {
      if (col.field && filters[col.field] !== undefined) {
        return {
          ...col,
          tableData: { ...col.tableData, filterValue: filters[col.field] },
        };
      }
      return col;
    });
  };

  /**
   * Step 4 — the only setState in this pipeline. Commits stamped rows, then
   * restoreState so filters / sort / page are not lost. Cell helperText still
   * comes from MTableEditField calling column.validate on render (unchanged).
   */
  const commitTableData = (tableRef, rows) => {
    tableRef.dataManager.setData(rows);
    tableRef.setState(
      { ...tableRef.dataManager.getRenderState() },
      () => restoreState(tableRef),
    );
  };

  /**
   * Multi-edit keystroke handler. Scope: keep `tableData.hasInvalid` in sync
   * with live column.validate (including uniqueness across in-progress rows).
   *
   * Does not change single-row edit(), add, selection, or filter render paths.
   * Apply/Save continue to gate only on hasInvalid; helperText is still rendered
   * by material-table from column.validate.
   *
   * Order is required:
   *   1. apply  — copy the field onto every editing row
   *   2. publish — expose that snapshot to getOriginalData() without rendering
   *   3. stamp   — hasInvalid per row from column.validate (siblings visible)
   *   4. commit  — one setState + restore filters/sort/page
   */
  const bulkEdit = (tableRef, value, p) => {
    if (p.columnDef?.unique && isMultiEditRef.current) return;

    saveState(tableRef);

    const rowsWithField = applyFieldToEditingRows(tableRef.state.originalData, {
      customOnChange: p.columnDef.customOnChange,
      field: p.columnDef.field,
      value,
    });

    publishTableData(tableRef, rowsWithField);

    const validateColumns = tableRef.props.columns.filter(column => column.validate);
    const rowsWithValidity = stampEditingValidity(rowsWithField, validateColumns);

    commitTableData(tableRef, rowsWithValidity);
  };

  const getCustomComponent = (col) => {
    const format = i18n.t('date:DateFormat');

    if (col.editComponent) return col.editComponent;

    const directEventBulkEdit = (...rest) => bulkEdit(materialTableRef.current, ...rest);
    const defaultProps = {
      ...col,
      bulkEdit: (e, ...rest) => bulkEdit(materialTableRef.current, e.target.value, ...rest),
      field: col.field,
      val,
      options,
      editing: 'always',
    };

    const directEventBulkEditProps = {
      ...defaultProps,
      bulkEdit: directEventBulkEdit,
    };

    const directEventBulkEditPropsDateRailroad = {
      ...defaultProps,
      dateWithoutKeyboard: true,
      bulkEdit: directEventBulkEdit,
    };

    const dateByTimestampProps = {
      ...defaultProps,
      bulkEdit: directEventBulkEdit,
      format: `${format}`,
      isDateTime: false,
      // views: ['hours', 'minutes', 'seconds'],
      noParseDate: true,
    };

    const dateTimeByTimestampProps = {
      ...defaultProps,
      bulkEdit: directEventBulkEdit,
      format: `${format} HH:mm:ss`,
      isDateTime: true,
      views: ['hours', 'minutes', 'seconds'],
      noParseDate: true,
    };
    // It maps the custom components,
    // to use it on column define column.type: 'customType' and customType: <type>
    const customComponents = {
      default: { editComponent: preCell(DefaultCell, directEventBulkEditProps) },
      time: { editComponent: preCell(TimeCell, defaultProps) },
      filterLookup: { editComponent: preCell(FilterLookupCell, defaultProps) },
      date: { editComponent: preCell(DateCell, directEventBulkEditProps) },
      dateRailroad: { editComponent: preCell(DateCell, directEventBulkEditPropsDateRailroad) },
      dateFuture: { editComponent: preCell(DateFutureCell, directEventBulkEditProps) },
      dateTimeByTimestamp: { editComponent: preCell(DateCell, dateTimeByTimestampProps) },
      dateByTimestamp: { editComponent: preCell(DateCell, dateByTimestampProps) },
      boolean: {
        editComponent: preCell(ActiveCell, directEventBulkEditProps),
        render: preCell(ActiveCell, { ...directEventBulkEditProps, editing: false }),
      },
    };

    return customComponents[col.customType]
      ? customComponents[col.customType]
      : customComponents.default;
  };

  const parseDataForLookup = (lookup, key) => {
    if (lookup) {
      return lookup.reduce((res, i) => { res[i[key]] = i.name; return res; }, {});
    }
    return undefined;
  };

  const reorderColumns = (cols) => {
    if (!options.cacheKey) return cols;
    const indexList = getColumnOrder(options.cacheKey, user.id);
    if (!indexList) return cols;
    if (indexList.length !== cols.length) {
      setColumnOrder(options.cacheKey, user.id, cols.map((_, i) => i));
      return cols;
    }
    const newCols = [...cols];
    const reorderedCols = indexList.map(i => newCols[i]);
    return reorderedCols;
  };

  const EMPTY_SERIALIZED_VALUES = new Set(['', 'null', 'undefined', '[]', '{}']);

  const isEmptySerializedValue = value => EMPTY_SERIALIZED_VALUES.has(String(value).trim());
  const normalizeSearchValue = value => String(value).toLowerCase().trim();
  const getLookupDisplayValue = (lookup, value) => lookup?.[value] ?? lookup?.[Number(value)];
  const parseMultiValueFilter = (value) => {
    if (value === null) return [];
    if (Array.isArray(value)) {
      return {
        filterValue: value.map(normalizeSearchValue).filter(token => token !== ''),
        isMultiFilter: true,
      };
    }
    return {
      filterValue: [normalizeSearchValue(value)],
      isMultiFilter: false,
    };
  };

  const getSearchCandidates = (col, rowData) => {
    const candidates = {
      searchValue: '',
      lookupValue: '',
    };
    const rawValue = rowData?.[col.field];

    if (!isEmptySerializedValue(rawValue)) {
      candidates.searchValue = normalizeSearchValue(rawValue);
    }

    if (col.lookup) {
      const lookupValue = getLookupDisplayValue(col.lookup, rawValue);
      if (!isEmptySerializedValue(lookupValue)) {
        candidates.lookupValue = normalizeSearchValue(lookupValue);
      }
    }

    return candidates;
  };

  const getCustomFilterAndSearch = (col) => {
    const originalFilterAndSearch = col.customFilterAndSearch;

    return (filterValue, rowData) => {
      const { searchValue, lookupValue } = getSearchCandidates(col, rowData);
      const {
        filterValue: selectedFilterValues,
        isMultiFilter,
      } = parseMultiValueFilter(filterValue);
      const normalizedFilterValue = normalizeSearchValue(filterValue);
      const originalMatch = originalFilterAndSearch?.(filterValue, rowData);

      if (rowData?.tableData?.editing === 'update') return true;
      if (isEmptySerializedValue(filterValue)) return true;

      if (col.lookup !== undefined) {
        const searchKeyFullMatch = selectedFilterValues.includes(searchValue);
        const searchValuePartialMatch = !isMultiFilter && selectedFilterValues.some(
          candidate => lookupValue.includes(candidate),
        );
        return searchKeyFullMatch || searchValuePartialMatch || originalMatch;
      }

      const hasPartialTextMatch = [searchValue, lookupValue].some((value) => {
        if (!value) return false;
        return value.includes(normalizedFilterValue);
      });

      return hasPartialTextMatch || originalMatch;
    };
  };

  const getSortValue = ({ row, field, lookupItems = null }) => {
    const isEditing = row.tableData?.editing === 'update';

    if (isEditing && lookupItems === null) {
      return row.tableData?.original?.[field] ?? row[field];
    }

    if (isEditing && lookupItems !== null) {
      const originalValue = row.tableData?.original?.[field] ?? row[field];
      return lookupItems[Number(originalValue)];
    }

    if (lookupItems !== null) {
      return lookupItems[Number(row[field])];
    }

    return row[field];
  };

  const isNumericSortValue = value => /^-?\d+(\.\d+)?$/.test(value);
  const isEmptySortValue = value => value === null || String(value).trim() === '' || value === undefined;
  const orderEditingFirst = ({ field, lookupItems = null, locale }) => (a, b) => {
    const aSortValue = getSortValue({ row: a, field, lookupItems }) ?? '';
    const bSortValue = getSortValue({ row: b, field, lookupItems }) ?? '';

    const aEmpty = isEmptySortValue(aSortValue);
    const bEmpty = isEmptySortValue(bSortValue);

    const aValue = String(aSortValue).trim();
    const bValue = String(bSortValue).trim();

    const aIsNum = isNumericSortValue(aValue);
    const bIsNum = isNumericSortValue(bValue);

    // asc: always show the empty value last
    if (aEmpty && bEmpty) return -1;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    // if both are numbers, compare them numerically
    if (aIsNum && bIsNum) {
      return Number(aValue) - Number(bValue);
    }

    // if one is a number and the other is not, the number comes first
    if (aIsNum && !bIsNum) return -1;
    if (!aIsNum && bIsNum) return 1;

    // if both are text, compare them locale-aware
    return aValue.localeCompare(bValue, locale, {
      sensitivity: 'base',
      numeric: true,
    });
  };

  // Adapt the this component to material-table api
  const parsedCols = applyFiltersToColumns(reorderColumns(columns).map((col) => {
    const lookupData = col.lookup;
    const lookupKey = col.lookupKey || 'id';
    // const direction = materialTableRef.current?.dataManager?.orderDirection;

    const lookupItems = parseDataForLookup(lookupData, lookupKey);
    const customSort = col.customSort || orderEditingFirst({ field: col.field, lookupItems });
    const customFilterAndSearch = getCustomFilterAndSearch({
      ...col,
      lookup: lookupItems,
    });

    let { render } = col;

    if (!render && col.customType === 'dateTimeByTimestamp') {
      render = (row) => {
        const ts = row[col.field];

        if (!ts) return '';

        return (new Date(ts)).toLocaleString();
      };
    }

    if (!render && col.customType === 'dateByTimestamp') {
      render = (row) => {
        const ts = row[col.field];

        if (!ts) return '';

        return (new Date(ts)).toLocaleDateString();
      };
    }


    return {
      ...col,
      initialEditValue: col.initialEditValue || '',
      lookupData,
      lookup: lookupItems,
      lookupKey,
      render,
      customSort,
      customFilterAndSearch,
      ...(col.unique ? {
        editable: (columnDef, rowData) => {
          if (isMultiEditRef.current) return false;
          return isEditableOutsideMultiEdit(col.editable, columnDef, rowData);
        },
      } : {}),
      ...getCustomComponent(col),
    };
  }));

  const onColumnDragged = (sourceIndex, destinationIndex) => {
    const indexList = getColumnOrder(options.cacheKey, user.id)
      || parsedCols.map((i, index) => index);
    const newColumnOrder = [...indexList];
    newColumnOrder.splice(sourceIndex, 1);
    newColumnOrder.splice(destinationIndex, 0, indexList[sourceIndex]);
    setColumnOrder(options.cacheKey, newColumnOrder, user.id);
  };

  const edit = (_event, tableRef, rowData, columnId = 'id') => {
    // It activates the mass editing mode
    // It doesn't activate the editing mode for the current row,
    // It only activates the mass editing mode
    saveState(tableRef);

    // const tableRef = materialTableRef.current;
    isMultiEditRef.current = true;

    const renderState = tableRef.state.originalData;
    const editColumns = tableRef.props.columns;

    const validateColumns = editColumns.filter(c => c.validate);
    const hasInvalid = validateColumns?.map(c => c.validate(rowData)?.isValid).includes(false);
    const rowList = rowData.length ? rowData : [rowData];

    const { currentPage: dmCurrentPage, page: dmPage } = tableRef.dataManager;

    const currentPage = propRest.page
      ?? dmCurrentPage
      ?? dmPage
      ?? 0;

    const newRenderData = [
      ...renderState.map(item => (rowList.find(row => row[columnId] === item[columnId])
        ? {
          ...item,
          tableData: {
            ...item.tableData,
            editing: 'update',
            original: { ...getOriginal(item) },
            hasInvalid,
          },
        }
        : item)),
    ];

    tableRef.dataManager.setData(newRenderData);
    tableRef.dataManager.changeCurrentPage(currentPage);

    const nextRenderState = tableRef.dataManager.getRenderState();

    tableRef.setState({
      ...nextRenderState,
      // currentPage,
      // query: {
      //   ...nextRenderState.query,
      //   page: currentPage,
      // },
    }, () => restoreState(tableRef));
  };

  const editable = defaultEditable ? {
    onRowUpdate: async (newData) => {
      // This action has two flows:
      // 1. Single edit - isMultiEditRef is false
      // 2. Multi edit - isMultiEditRef is true
      try {
        if (!isMultiEditRef.current) {
          onChange([newData]);
          return;
        }
        pendingRowUpdatesRef.current[newData.id] = newData;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    // this feature don't support for mass editing, options.selection = true;
    isEditable: (rowData) => {
      const tableRef = materialTableRef.current;
      const columnsIsEditable = tableRef?.props.columns;

      if (!columnsIsEditable) return;

      const validateColumns = columnsIsEditable.filter(c => c.validate);

      const hasInvalid = validateColumns?.map(c => c.validate(rowData).isValid).includes(false);

      const rowDataWithValidation = {
        ...rowData,
        tableData: {
          hasInvalid,
        },
      };

      // eslint-disable-next-line consistent-return
      return isEditable(rowDataWithValidation);
    },
    isEditHidden: rowData => options.editableRowValidation?.(rowData),
  } : {};

  const commitChangesIcon = (
    options.disableSaveButton ? icons.DisabledCheckCircle : icons.CheckCircle
  );

  const commitChangesTooltip = (
    options.disableSaveButton
      ? translate('validation:FormWasNotFilledCorrectly')
      : translate('common:Commit Changes')
  );

  const handleClearAllSelectedItems = () => {
    const tableRef = materialTableRef.current;
    if (!tableRef) return;

    tableRef.dataManager.data.forEach((_, index) => {
      const dataRow = tableRef.dataManager.data[index];
      if (dataRow.tableData?.checked) {
        dataRow.tableData.checked = false;
      }
    });

    tableRef.dataManager.selectedCount = 0;
    tableRef.setState({ ...tableRef.dataManager.getRenderState() });
  };

  return (
    <div>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <div className="table-wrapper">
          <MaterialTable
            icons={icons}
            columns={parsedCols}
            data={parsedData(data)}
            tableRef={materialTableRef}
            components={{
              EditRow: props => EditableRow(props, materialTableRef.current),
              Header: props => Header(props, materialTableRef.current),
            }}
            onOrderChange={(orderBy, orderDirection) => {
              const tableRef = materialTableRef.current;
              if (options.selection) {
                handleClearAllSelectedItems();
              }
              if (options.onOrderChange) {
                options.onOrderChange({ tableRef, orderBy, orderDirection });
              }
              saveState(tableRef);
            }}
            onFilterChange={() => {
              if (options.selection) {
                handleClearAllSelectedItems();
              }
              saveState(materialTableRef.current);
            }}
            onSearchChange={() => {
              if (options.selection) {
                handleClearAllSelectedItems();
              }
              if (options.onSearchChange) options.onSearchChange(materialTableRef.current);
              saveState(materialTableRef.current);
            }}
            onChangePage={(page) => {
              if (options.selection) {
                handleClearAllSelectedItems();
              }
              if (options.onChangePage) {
                options.onChangePage(page, materialTableRef.current);
              }
              filterStateRef.current.page = page;
            }}
            onColumnDragged={options.cacheKey ? onColumnDragged : undefined}
            actions={options.hideEditColumn ? null : [
              {
                hidden: options.hideCommit,
                icon: commitChangesIcon,
                isFreeAction: true,
                tooltip: commitChangesTooltip,
                onClick: (event) => {
                  const tableRef = materialTableRef.current;

                  const hasInvalidFields = tableRef.state.renderData.some(
                    curData => curData.tableData.hasInvalid,
                  );

                  if (hasInvalidFields) return;

                  saveState(tableRef);

                  // TODO Implementar a paginação nessa action
                  const { currentPage: dmCurrentPage, page: dmPage } = tableRef.dataManager;

                  const currentPage = propRest.page
                    ?? dmCurrentPage
                    ?? dmPage
                    ?? 0;

                  onApply(event, currentPage);
                  isMultiEditRef.current = false;

                  tableRef.dataManager.changeCurrentPage(currentPage);
                  restoreState(tableRef);
                },
                disabled: options.disableSaveButton,
              },
              {
                hidden: options.hideReload,
                icon: icons.Reload,
                isFreeAction: true,
                tooltip: translate('common:Discard Changes'),
                onClick: (event) => {
                  isMultiEditRef.current = false;
                  onDiscard(event);
                },
              },
              {
                icon: icons.Clear,
                hidden: !onInactivateAll,
                tooltip: translate('common:InactivateAllSelectedItems'),
                onClick: (_, items) => onInactivateAll(items),
              },
              {
                icon: icons.Add,
                isFreeAction: true,
                tooltip: translate('common:Add'),
                hidden: options.hideAdd,
                onClick: () => {
                  const tableRef = materialTableRef.current;
                  const tableRefPropColumns = tableRef.props.columns;

                  const newDataParsed = {
                    ...columns.reduce((acc, key) => ({ ...acc, [key.field]: '' }), {}),
                    ...propRest.initialFormData || {},
                  };

                  const validateColumns = tableRefPropColumns.filter(c => c.validate);

                  const hasInvalid = validateColumns?.map(
                    c => c.validate(newDataParsed).isValid,
                  ).includes(false);

                  const newDataParsedWithValidation = {
                    ...newDataParsed,
                    tableData: {
                      hasInvalid,
                    },
                  };


                  tableRef.dataManager.changeCurrentPage(0);
                  onAdd(newDataParsedWithValidation);
                },
              },
              rowData => ({
                hidden: !options.selection,
                disabled: options.editableRowValidation?.(rowData),
                icon: icons.Check,
                tooltip: translate('common:Save'),
                onClick: () => {
                  const tableRef = materialTableRef.current;

                  saveState(tableRef);

                  const hasInvalidFields = tableRef.state.renderData.some(
                    curData => curData.tableData.hasInvalid,
                  );

                  if (hasInvalidFields) return;

                  const rowList = tableRef.state.renderData
                    .filter(({ tableData: { checked } }) => checked)
                    .map(item => (
                      { ...item, tableData: undefined }
                    ));

                  isMultiEditRef.current = false;
                  onChange(rowList);
                  restoreState(tableRef);
                },
              }
              ),
              rowData => ({
                hidden: !options.selection,
                disabled: isSelectionDisabled(rowData),
                icon: icons.Edit,
                tooltip: translate('common:Edit'),
                onClick: (_event, selectedRow) => {
                  const tableRef = materialTableRef.current;
                  saveState(tableRef);
                  edit(_event, tableRef, selectedRow, options.columnId || 'id');
                },
              }),
              {
                icon: icons.Clear,
                hidden: !options.selection,
                tooltip: translate('common:ClearAllSelectedItems'),
                onClick: () => {
                  const tableRef = materialTableRef.current;
                  isMultiEditRef.current = false;

                  saveState(tableRef);

                  const rowList = tableRef.state.renderData;
                  const newRenderData = rowList.map((item) => {
                    const tableData = {
                      ...item.tableData,
                      editing: null,
                      checked: false,
                    };
                    return item.tableData.original
                      ? { ...item, ...item.tableData.original, tableData }
                      : { ...item, tableData };
                  });

                  // TODO Implementar a paginação nessa action
                  // const { currentPage: dmCurrentPage, page: dmPage } = tableRef.dataManager;

                  // const currentPage = propRest.page
                  //   ?? dmCurrentPage
                  //   ?? dmPage
                  //   ?? 0;

                  // tableRef.dataManager.changeCurrentPage(0);
                  tableRef.dataManager.setData(newRenderData);
                  tableRef.setState({
                    ...tableRef.dataManager.getRenderState(),
                  }, () => restoreState(tableRef));
                },
              },
              {
                icon: icons.Delete,
                hidden: !options.selection || options.hideDelete,
                tooltip: translate('common:RemoveAllSelectedItems'),
                onClick: (_, items) => onDelete(items),
              },
              {
                hidden: !onExport,
                icon: icons.FileDownload,
                isFreeAction: true,
                tooltip: translate('common:Export'),
                onClick: event => onExport(event),
              },
              {
                hidden: !onImport,
                icon: icons.FileUpload,
                isFreeAction: true,
                tooltip: translate('common:Import'),
                onClick: event => onImport(event),
              },
              ...extraActions,
            ]}
            cellEditable={
              options.cellEditable
                ? {
                  onCellEditApproved: (newData, rowData, columnDef) => new Promise((resolve) => {
                    onChange({ ...rowData, [columnDef.field]: newData });
                    resolve();
                  }),
                }
                : null
            }
            editable={options.hideEditColumn ? null : editable}
            options={{
              pageSizeOptions: [10, 20, 50, options.pageSize ? options.pageSize : 100],
              searchFieldAlignment: 'left',
              ...config,
              ...options,
              emptyRowsWhenPaging: !options.emptyRowsWhenPaging,
              maxBodyHeight: '620px',
              selectionProps: rowData => ({
                disabled: isSelectionDisabled(rowData),
              }),
            }}
            localization={localization(translate)}
            {...propRest}
          />
        </div>
      </MuiThemeProvider>
    </div>
  );
});

PF2MDataTable.whyDidYouRender = true;

const ActionPropType = PropTypes.shape({
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.elementType,
  ]),
  iconProps: PropTypes.object,
  isFreeAction: PropTypes.bool,
  onClick: PropTypes.func,
  tooltip: PropTypes.string,
});

PF2MDataTable.propTypes = {
  data: PropTypes.array,
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
  onDiscard: PropTypes.func,
  onApply: PropTypes.func,
  onDelete: PropTypes.func,
  onInactivateAll: PropTypes.func,
  columns: PropTypes.array,
  options: PropTypes.object,
  extraActions: PropTypes.arrayOf(ActionPropType),
  defaultEditable: PropTypes.bool,
  onExport: PropTypes.func,
  onImport: PropTypes.func,
  isEditable: PropTypes.func,
};

PF2MDataTable.defaultProps = {
  data: [],
  columns: [],
  onAdd: () => { },
  onChange: () => { },
  onDiscard: () => { },
  onApply: () => { },
  onDelete: () => { },
  onInactivateAll: null,
  options: {},
  onExport: undefined,
  onImport: undefined,
  extraActions: [],
  defaultEditable: true,
  isEditable: () => true,
};

export default PF2MDataTable;
export {
  icons,
};
